import { useCallback, useEffect, useState } from 'react'
import { usePlaidLink } from 'react-plaid-link'
import * as plaidCommands from '@healthshare/shared/commands/plaid'
import { isExpoApp, runPlaidHostedLink } from '../../lib/plaid/hostedLink'
import { useAppDispatch, useAppSelector } from './reduxToolkit'
import { useBackendApi } from './useBackendApi'
import { useBackendAuth } from './useBackendAuth'
import { useSubscriptionPrompt } from './useSubscriptionPrompt'
import { useSentry } from '../providers/sentry'
import { addPlaidItem } from '../reduxToolkit/plaidSlice'

/**
 * Starts the Plaid "link a new account" flow from anywhere in the app.
 *
 * Encapsulates the create-link-token → Plaid Link → exchange-public-token round
 * trip (and the Expo Hosted Link variant), dispatching `addPlaidItem` on
 * success. The same subscription gate used on the Connected Accounts page
 * applies here: linking a second account prompts for a subscription.
 *
 * Callers render the returned `SubscriptionPromptModal` and wire a button to
 * `startLink`; `linking` is true while the flow is in flight so the button can
 * be disabled.
 */
export function usePlaidLinkFlow() {
  const dispatch = useAppDispatch()
  const api = useBackendApi()
  // Still needed for the Hosted Link bridge below, which stays on raw fetch --
  // see lib/plaid/hostedLink.ts, not part of the Stage 2 command surface.
  const { getAuthHeaders } = useBackendAuth()
  const { captureException } = useSentry()
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const items = useAppSelector((s) => s.plaid.items)

  const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
    trigger: 'custom',
    shouldPrompt: () => items.length >= 1,
    title: 'Subscription Required',
    message: 'Linking multiple bank accounts requires a subscription. Upgrade to connect additional accounts.',
  })

  const [error, setError] = useState<string | null>(null)
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [linking, setLinking] = useState(false)

  const onExchangeSuccess = useCallback(async (
    publicToken: string,
    institution: { name: string; institution_id: string } | null,
  ) => {
    try {
      const data = await plaidCommands.exchangePublicToken(api, {
        publicToken,
        membershipId: membershipId!,
        // Plaid's /accounts/get omits institution_name; relay it from
        // Plaid Link's onSuccess metadata.
        institutionId: institution?.institution_id ?? null,
        institutionName: institution?.name ?? null,
      })
      dispatch(addPlaidItem({
        id: data.plaid_item_id,
        institution_name: data.institution_name,
        status: 'active',
        last_synced_at: null,
        // persistExchangedItem's response never included this field -- the
        // fallback was always taken, so this preserves that exactly.
        sync_started_at: null,
        accounts: data.accounts ?? [],
      }))
    } catch (e) {
      console.error('Plaid exchange failed', e)
      captureException(e, { tags: { feature: 'plaid', plaid_op: 'exchange-public-token' } })
      setError('Unable to finish connecting that account.')
    } finally {
      setLinkToken(null)
      setLinking(false)
    }
  }, [api, membershipId, dispatch, captureException])

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken, metadata) => {
      void onExchangeSuccess(publicToken, metadata.institution ?? null)
    },
    onExit: () => {
      setLinkToken(null)
      setLinking(false)
    },
  })

  useEffect(() => {
    if (linkToken && ready) open()
  }, [linkToken, ready, open])

  const startLink = async () => {
    if (!checkSubscription()) return
    if (!membershipId) return
    setError(null)
    setLinking(true)
    // In the Expo WebView, Plaid Link's OAuth handoff can't complete inside
    // the WebView, so use Hosted Link (system browser + universal-link return).
    if (isExpoApp()) {
      try {
        const outcome = await runPlaidHostedLink({ authHeaders: getAuthHeaders(), membershipId })
        if (outcome.status === 'connected') {
          dispatch(addPlaidItem({
            id: outcome.data.plaid_item_id,
            institution_name: outcome.data.institution_name,
            status: 'active',
            last_synced_at: null,
            sync_started_at: outcome.data.sync_started_at ?? null,
            accounts: outcome.data.accounts ?? [],
          }))
        }
      } catch (e) {
        console.error('Plaid hosted link failed', e)
        captureException(e, { tags: { feature: 'plaid', plaid_op: 'hosted-link' } })
        setError('Unable to finish connecting that account.')
      } finally {
        setLinking(false)
      }
      return
    }
    try {
      const data = await plaidCommands.createLinkToken(api, { membershipId })
      if (!data.link_token) throw new Error('No link_token')
      setLinkToken(data.link_token)
    } catch (e) {
      console.error('Plaid create-link-token failed', e)
      captureException(e, { tags: { feature: 'plaid', plaid_op: 'create-link-token' } })
      setError('Unable to open the connection window.')
      setLinking(false)
    }
  }

  const clearError = () => setError(null)

  return { startLink, linking, error, clearError, SubscriptionPromptModal }
}
